import { useEffect, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useCampaignStore } from '@/stores';
import { useBillingStatus } from '@/hooks/queries';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Loader2, AlertCircle, RefreshCw } from 'lucide-react';

const TIMEOUT_MS = 10_000;
const POLL_INTERVAL_MS = 2_000;

export function SubscribeCompletePage() {
  const selectedCampaign = useCampaignStore((s) => s.selectedCampaign);
  const selectCampaign = useCampaignStore((s) => s.selectCampaign);
  const navigate = useNavigate();
  const [timedOut, setTimedOut] = useState(false);

  const { data: status, isLoading, isError } = useBillingStatus({
    refetchInterval: POLL_INTERVAL_MS,
  });

  // No campaign selected → redirect to /app
  useEffect(() => {
    if (!selectedCampaign) {
      navigate({ to: '/app' });
    }
  }, [selectedCampaign, navigate]);

  // Timeout after ~10 seconds
  useEffect(() => {
    const timer = setTimeout(() => {
      setTimedOut(true);
    }, TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, []);

  // When status becomes Active → update store + redirect
  useEffect(() => {
    if (
      status?.subscriptionStatus === 'Active' &&
      selectedCampaign &&
      selectedCampaign.subscriptionStatus !== 'Active'
    ) {
      selectCampaign({
        ...selectedCampaign,
        subscriptionStatus: 'Active',
      });
      navigate({ to: '/app' });
    }
  }, [status, selectedCampaign, selectCampaign, navigate]);

  if (!selectedCampaign) {
    return null;
  }

  // Error state
  if (isError) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6 text-center space-y-4">
            <AlertCircle className="h-10 w-10 mx-auto text-red-500" />
            <p className="text-sm text-red-600">
              Something went wrong verifying your payment. Please try refreshing the page.
            </p>
            <Button variant="outline" onClick={() => window.location.reload()}>
              <RefreshCw className="mr-2 h-4 w-4" />
              Refresh
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Timeout state — still polling but warn user
  if (timedOut && status?.subscriptionStatus !== 'Active') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6 text-center space-y-4">
            <Loader2 className="h-10 w-10 mx-auto animate-spin text-blue-600" />
            <h2 className="text-lg font-semibold text-gray-900">{selectedCampaign.name}</h2>
            <p className="text-sm text-gray-500">
              Taking longer than expected, please refresh the page.
            </p>
            <Button variant="outline" onClick={() => window.location.reload()}>
              <RefreshCw className="mr-2 h-4 w-4" />
              Refresh
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Normal polling state
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
      <Card className="w-full max-w-md">
        <CardContent className="pt-6 text-center space-y-4">
          <Loader2 className="h-10 w-10 mx-auto animate-spin text-blue-600" />
          <h2 className="text-lg font-semibold text-gray-900">{selectedCampaign.name}</h2>
          <p className="text-sm text-gray-500">Setting up your account...</p>
        </CardContent>
      </Card>
    </div>
  );
}
